/**
 * RULES_AY_2026_27 — versioned rate config for the Income Tax Act 2025
 * (Finance Act 2026), AY 2026-27 / FY 2026-27.
 *
 * Source: EY FAQ "New Tax Regime v/s Old Tax Regime" (May 2026).
 * Open items pending verification:
 *  - Senior / super-senior old-regime slabs
 *  - Post-FA-2026 capital-gains rates
 *  - Whether the 37% top surcharge in the new regime is correct or an EY
 *    doc error (changed under FA 2025)
 *
 * Mirrors the JS prototype's RULES_AY_2026_27 1:1 — keep this file pure data
 * with no DOM/React/import side-effects so the engine is testable in isolation.
 */

export interface SlabBand    { upTo: number; rate: number; }
export interface SurchargeBand { exceeds: number; rate: number; }
export interface RebateConfig { thresholdTI: number; maxRebate: number; marginalRelief: boolean; }

export interface RegimeRules {
  standardDeduction: number;
  slabs?:            SlabBand[];          // new regime (single set)
  slabsBelow60?:     SlabBand[];          // old regime (per age band)
  slabs60to80?:      SlabBand[];
  slabsAbove80?:     SlabBand[];
  rebate87a:         RebateConfig;
  surcharge:         SurchargeBand[];
}

export interface CapitalGainsRules {
  ltcgEquity:     { rate: number; exemption: number; sec: string };
  stcgEquity:     { rate: number; exemption: number; sec: string };
  ltcgDebt:       { rate: number; exemption: number; sec: string };
  ltcgRealEstate: { rate: number; exemption: number; sec: string };
  surchargeCap:   number;
}

export interface Rules {
  ay:              string;
  fy:              string;
  source:          string;
  newRegime:       RegimeRules;
  oldRegime:       RegimeRules;
  capitalGains:    CapitalGainsRules;
  cess:            number;
  hraMetroCities:  string[];
  hraMetroSource:  string;
}

export const RULES_AY_2026_27: Rules = {
  ay:     '2026-27',
  fy:     '2026-27',
  source: 'EY FAQ May 2026, post Finance Act 2026',
  newRegime: {
    standardDeduction: 75000,
    slabs: [
      { upTo:   400000, rate: 0    },
      { upTo:   800000, rate: 0.05 },
      { upTo:  1200000, rate: 0.10 },
      { upTo:  1600000, rate: 0.15 },
      { upTo:  2000000, rate: 0.20 },
      { upTo:  2400000, rate: 0.25 },
      { upTo: Infinity, rate: 0.30 },
    ],
    rebate87a: { thresholdTI: 1200000, maxRebate: 60000, marginalRelief: true },
    surcharge: [
      { exceeds:  5000000, rate: 0.10 },
      { exceeds: 10000000, rate: 0.15 },
      { exceeds: 20000000, rate: 0.25 },
      { exceeds: 50000000, rate: 0.37 },
    ],
  },
  oldRegime: {
    standardDeduction: 50000,
    slabsBelow60: [
      { upTo:   250000, rate: 0    },
      { upTo:   500000, rate: 0.05 },
      { upTo:  1000000, rate: 0.20 },
      { upTo: Infinity, rate: 0.30 },
    ],
    slabs60to80: [
      { upTo:   300000, rate: 0    },
      { upTo:   500000, rate: 0.05 },
      { upTo:  1000000, rate: 0.20 },
      { upTo: Infinity, rate: 0.30 },
    ],
    slabsAbove80: [
      { upTo:   500000, rate: 0    },
      { upTo:  1000000, rate: 0.20 },
      { upTo: Infinity, rate: 0.30 },
    ],
    rebate87a: { thresholdTI: 500000, maxRebate: 12500, marginalRelief: true },
    surcharge: [
      { exceeds:  5000000, rate: 0.10 },
      { exceeds: 10000000, rate: 0.15 },
      { exceeds: 20000000, rate: 0.25 },
      { exceeds: 50000000, rate: 0.37 },
    ],
  },
  capitalGains: {
    ltcgEquity:     { rate: 0.125, exemption: 125000, sec: 'S.112A' },
    stcgEquity:     { rate: 0.20,  exemption: 0,      sec: 'S.111A' },
    ltcgDebt:       { rate: 0.125, exemption: 0,      sec: 'S.112'  },
    ltcgRealEstate: { rate: 0.125, exemption: 0,      sec: 'S.112'  },
    surchargeCap:   0.15,
  },
  cess: 0.04,
  // Draft IT Rules 2026 Rule 279 — 8-city metro list, applicable from
  // 1-Apr-2026 (FY 2026-27). Pending final notification.
  hraMetroCities: [
    'Delhi', 'Mumbai', 'Chennai', 'Kolkata',
    'Bengaluru', 'Hyderabad', 'Pune', 'Ahmedabad',
  ],
  hraMetroSource: 'Draft IT Rules 2026, Rule 279 (pending final notification)',
};
